import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import {
    GET_PRIVACY_POLICY,
    CREATE_PRIVACY_POLICY,
    UPDATE_PRIVACY_POLICY,
    ADD_PRIVACY_SECTION,
    UPDATE_PRIVACY_SECTION,
    DELETE_PRIVACY_SECTION,
    ADD_PRIVACY_SUB_SECTION,
    UPDATE_PRIVACY_SUB_SECTION,
    DELETE_PRIVACY_SUB_SECTION,
} from '@/services/ApiRoutes';
import { get, post, put, del } from '@/services/ApiService';

export interface SubSection {
    id?: string;
    _id?: string;
    title: string;
    content: string;
}

export interface Section {
    id?: string;
    _id?: string;
    title: string;
    order: number;
    subSections: SubSection[];
}

export interface PrivacyPolicy {
    id?: string;
    _id?: string;
    lastUpdated: string;
    tableOfContents: string[];
    sections: Section[];
    createdAt?: string;
    updatedAt?: string;
}

interface PrivacyState {
    data: PrivacyPolicy | null;
    loading: boolean;
    error: string | null;
    sectionLoading: boolean;
}

const initialState: PrivacyState = {
    data: null,
    loading: false,
    error: null,
    sectionLoading: false,
};

// Async Thunks
export const fetchPrivacyPolicy = createAsyncThunk(
    'privacy/fetch',
    async (_, { rejectWithValue }) => {
        try {
            const response = await get(GET_PRIVACY_POLICY);
            return response.data?.data || response.data;
        } catch (error: any) {
            return rejectWithValue(error.response?.data?.message || 'Failed to fetch privacy policy');
        }
    }
);

export const createPrivacyPolicy = createAsyncThunk(
    'privacy/create',
    async (data: { lastUpdated: string; sections: Section[] }, { rejectWithValue }) => {
        try {
            const response = await post(CREATE_PRIVACY_POLICY, data);
            return response.data?.data || response.data;
        } catch (error: any) {
            return rejectWithValue(error.response?.data?.message || 'Failed to create privacy policy');
        }
    }
);

export const updatePrivacyPolicy = createAsyncThunk(
    'privacy/update',
    async (data: { lastUpdated: string; sections: Section[] }, { rejectWithValue }) => {
        try {
            const response = await put(UPDATE_PRIVACY_POLICY, data);
            return response.data?.data || response.data;
        } catch (error: any) {
            return rejectWithValue(error.response?.data?.message || 'Failed to update privacy policy');
        }
    }
);

export const addPrivacySection = createAsyncThunk(
    'privacy/addSection',
    async (data: { title: string; order?: number; subSections?: SubSection[] }, { rejectWithValue }) => {
        try {
            const response = await post(ADD_PRIVACY_SECTION, data);
            return response.data?.data || response.data;
        } catch (error: any) {
            return rejectWithValue(error.response?.data?.message || 'Failed to add section');
        }
    }
);

export const updatePrivacySection = createAsyncThunk(
    'privacy/updateSection',
    async ({ sectionId, data }: { sectionId: string; data: { title?: string; order?: number } }, { rejectWithValue }) => {
        try {
            const response = await put(UPDATE_PRIVACY_SECTION(sectionId), data);
            return response.data?.data || response.data;
        } catch (error: any) {
            return rejectWithValue(error.response?.data?.message || 'Failed to update section');
        }
    }
);

export const deletePrivacySection = createAsyncThunk(
    'privacy/deleteSection',
    async (sectionId: string, { rejectWithValue }) => {
        try {
            const response = await del(DELETE_PRIVACY_SECTION(sectionId));
            return response.data?.data || response.data;
        } catch (error: any) {
            return rejectWithValue(error.response?.data?.message || 'Failed to delete section');
        }
    }
);

export const addPrivacySubSection = createAsyncThunk(
    'privacy/addSubSection',
    async ({ sectionId, data }: { sectionId: string; data: { title: string; content: string } }, { rejectWithValue }) => {
        try {
            const response = await post(ADD_PRIVACY_SUB_SECTION(sectionId), data);
            return response.data?.data || response.data;
        } catch (error: any) {
            return rejectWithValue(error.response?.data?.message || 'Failed to add sub-section');
        }
    }
);

export const updatePrivacySubSection = createAsyncThunk(
    'privacy/updateSubSection',
    async (
        { sectionId, subId, data }: { sectionId: string; subId: string; data: { title?: string; content?: string } },
        { rejectWithValue }
    ) => {
        try {
            const response = await put(UPDATE_PRIVACY_SUB_SECTION(sectionId, subId), data);
            return response.data?.data || response.data;
        } catch (error: any) {
            return rejectWithValue(error.response?.data?.message || 'Failed to update sub-section');
        }
    }
);

export const deletePrivacySubSection = createAsyncThunk(
    'privacy/deleteSubSection',
    async ({ sectionId, subId }: { sectionId: string; subId: string }, { rejectWithValue }) => {
        try {
            const response = await del(DELETE_PRIVACY_SUB_SECTION(sectionId, subId));
            return response.data?.data || response.data;
        } catch (error: any) {
            return rejectWithValue(error.response?.data?.message || 'Failed to delete sub-section');
        }
    }
);

const privacySlice = createSlice({
    name: 'privacy',
    initialState,
    reducers: {
        clearError: (state) => {
            state.error = null;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchPrivacyPolicy.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchPrivacyPolicy.fulfilled, (state, action) => {
                state.loading = false;
                state.data = action.payload;
            })
            .addCase(fetchPrivacyPolicy.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload as string;
            })
            .addCase(createPrivacyPolicy.pending, (state) => {
                state.sectionLoading = true;
                state.error = null;
            })
            .addCase(createPrivacyPolicy.fulfilled, (state, action) => {
                state.sectionLoading = false;
                state.data = action.payload;
            })
            .addCase(createPrivacyPolicy.rejected, (state, action) => {
                state.sectionLoading = false;
                state.error = action.payload as string;
            })
            .addCase(updatePrivacyPolicy.pending, (state) => {
                state.sectionLoading = true;
                state.error = null;
            })
            .addCase(updatePrivacyPolicy.fulfilled, (state, action) => {
                state.sectionLoading = false;
                state.data = action.payload;
            })
            .addCase(updatePrivacyPolicy.rejected, (state, action) => {
                state.sectionLoading = false;
                state.error = action.payload as string;
            })
            .addCase(addPrivacySection.pending, (state) => {
                state.sectionLoading = true;
            })
            .addCase(addPrivacySection.fulfilled, (state, action) => {
                state.sectionLoading = false;
                state.data = action.payload;
            })
            .addCase(addPrivacySection.rejected, (state, action) => {
                state.sectionLoading = false;
                state.error = action.payload as string;
            })
            .addCase(updatePrivacySection.fulfilled, (state, action) => {
                state.data = action.payload;
            })
            .addCase(deletePrivacySection.fulfilled, (state, action) => {
                state.data = action.payload;
            })
            .addCase(addPrivacySubSection.fulfilled, (state, action) => {
                state.data = action.payload;
            })
            .addCase(updatePrivacySubSection.fulfilled, (state, action) => {
                state.data = action.payload;
            })
            .addCase(deletePrivacySubSection.fulfilled, (state, action) => {
                state.data = action.payload;
            });
    },
});

export const { clearError } = privacySlice.actions;
export default privacySlice.reducer;
